import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    IconButton,
    Paper,
    Tabs,
    Tab,
    useTheme,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    Tooltip,
    Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { useNavigate, useParams } from 'react-router-dom';
import { dataPrincipalsService, DataPrincipalDetails as DataPrincipalDetailsType } from '../../services/dataPrincipals-service';
import { vaultService } from '../../services/vault-service';
import { Global } from '@emotion/react';

// Placeholder tab components (to be implemented in Phase 5)
const OverviewTab = React.lazy(() => import('./components/OverviewTab'));
const ConsentHistoryTab = React.lazy(() => import('./components/ConsentHistoryTab'));
const ConsentBreakdownTab = React.lazy(() => import('./components/ConsentBreakdownTab'));
const RightsRequestsTab = React.lazy(() => import('./components/RightsRequestsTab'));
const AssignedNoticesTab = React.lazy(() => import('./components/AssignedNoticesTab'));
const DataElementsTab = React.lazy(() => import('./components/DataElementsTab'));
const ActivityLogTab = React.lazy(() => import('./components/ActivityLogTab'));

const DataPrincipalDetails: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { dpId } = useParams<{ dpId: string }>();

    // State management
    const [dpData, setDpData] = useState<DataPrincipalDetailsType | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Vault popup state
    const [viewValueDialog, setViewValueDialog] = useState(false);
    const [actualValue, setActualValue] = useState<string>('');
    const [maskedToken, setMaskedToken] = useState<string>('');
    const [loadingValue, setLoadingValue] = useState(false);
    const [valueError, setValueError] = useState<string>('');
    const [copySuccess, setCopySuccess] = useState(false);

    // Tab configuration
    const tabs = [
        { label: 'Overview', value: 0 },
        { label: 'Consent History', value: 1 },
        { label: 'Consent Breakdown', value: 2 },
        { label: 'Rights Requests', value: 3 },
        { label: 'Assigned Notices', value: 4 },
        { label: 'Data Elements', value: 5 },
        { label: 'Activity Log', value: 6 }
    ];

    // Fetch DP data
    useEffect(() => {
        const fetchDPData = async () => {
            if (!dpId) return;

            try {
                setLoading(true);
                setError(null);
                const data = await dataPrincipalsService.getDataPrincipal(parseInt(dpId));
                setDpData(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch data principal details');
            } finally {
                setLoading(false);
            }
        };

        fetchDPData();
    }, [dpId]);

    // Handle URL tab parameter
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        if (tabParam) {
            const tabIndex = tabs.findIndex(tab => tab.label.toLowerCase().replace(/\s+/g, '-') === tabParam);
            if (tabIndex !== -1) {
                setActiveTab(tabIndex);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // tabs is a constant array, no need to include in deps

    const handleBackClick = () => {
        navigate('/workbench/data-principals');
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        // Update URL without reload
        const tabName = tabs[newValue].label.toLowerCase().replace(/\s+/g, '-');
        navigate(`/workbench/data-principals/${dpId}?tab=${tabName}`, { replace: true });
    };

    // Vault handling functions
    const handleViewIdentifier = async (vaultId: number) => {
        if (!vaultId) return;

        setViewValueDialog(true);
        setLoadingValue(true);
        setValueError('');
        setActualValue('');
        setMaskedToken('');

        try {
            // Fetch both actual value and masked token in parallel
            const [actualResponse, maskedResponse] = await Promise.allSettled([
                vaultService.getActualValue(vaultId),
                vaultService.getMaskedToken(vaultId)
            ]);

            // Handle actual value response
            if (actualResponse.status === 'fulfilled') {
                setActualValue(actualResponse.value.originalValue);
            } else {
                console.error('Failed to get actual value:', actualResponse.reason);
                if (actualResponse.reason?.response?.status === 403) {
                    setActualValue('Insufficient permissions to view actual value');
                } else {
                    setActualValue('Failed to retrieve actual value');
                }
            }

            // Handle masked token response
            if (maskedResponse.status === 'fulfilled') {
                setMaskedToken(maskedResponse.value.partiallyMaskedToken);
            } else {
                console.error('Failed to get masked token:', maskedResponse.reason);
                if (maskedResponse.reason?.response?.status === 403) {
                    setMaskedToken('Insufficient permissions to view masked token');
                } else {
                    setMaskedToken('Failed to retrieve masked token');
                }
            }
        } catch (error) {
            console.error('Error fetching vault values:', error);
            setValueError('An error occurred while retrieving the data. Please try again.');
        } finally {
            setLoadingValue(false);
        }
    };

    const handleCopyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopySuccess(true);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    const handleCloseDialog = () => {
        setViewValueDialog(false);
        setActualValue('');
        setMaskedToken('');
        setValueError('');
    };

    // Format identifier for display
    const formatIdentifier = (): string => {
        if (!dpData) return 'N/A';
        if (dpData.isTest && dpData.identifierValue) {
            return dpData.identifierValue;
        }
        if (dpData.vaultId) {
            return `vault:${dpData.vaultId}`;
        }
        return 'N/A';
    };

    // Get consent status description
    const getConsentStatusDescription = (status: string): string => {
        switch (status.toUpperCase()) {
            case 'ACTIVE':
                return 'Has at least one active CONSENT-based purpose with consent given, not expired, and not withdrawn.';
            case 'WITHDRAWN':
                return 'All CONSENT-based purposes that had consent given have been withdrawn by the data principal.';
            case 'EXPIRED':
                return 'All CONSENT-based purposes with consent given have expired based on their retention expiration dates.';
            case 'INACTIVE':
                return 'Has CONSENT-based purposes but none are currently active (consent denied, expired, or withdrawn).';
            case 'UNKNOWN':
                return 'No CONSENT-based consent records found for this data principal.';
            default:
                return '';
        }
    };

    // Get consent status color
    const getConsentStatusColor = (status: string) => {
        if (theme.palette.mode === 'dark') {
            switch (status.toUpperCase()) {
                case 'ACTIVE':
                    return { bg: '#1B5E20', color: '#81C784', border: '#28A745' };
                case 'WITHDRAWN':
                    return { bg: '#4A1E1E', color: '#E57373', border: '#DC3545' };
                case 'EXPIRED':
                    return { bg: '#3E3B00', color: '#FFD54F', border: '#FFC107' };
                case 'INACTIVE':
                    return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
                default:
                    return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
            }
        } else {
            switch (status.toUpperCase()) {
                case 'ACTIVE':
                    return { bg: '#D4EDDA', color: '#155724', border: '#28A745' };
                case 'WITHDRAWN':
                    return { bg: '#F8D7DA', color: '#721C24', border: '#DC3545' };
                case 'EXPIRED':
                    return { bg: '#FFF3CD', color: '#856404', border: '#FFC107' };
                case 'INACTIVE':
                    return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
                default:
                    return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
            }
        }
    };

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error || !dpData) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Error Loading Data Principal
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        {error || 'Data principal not found'}
                    </Typography>
                    <Button variant="contained" onClick={handleBackClick} sx={{ mt: 2 }}>
                        Back to List
                    </Button>
                </Box>
            </Container>
        );
    }

    const statusColors = getConsentStatusColor(dpData.overallConsentStatus);

    return (
        <Container maxWidth="xl">
            <Global
                styles={{
                    body: {
                        backgroundColor: theme.palette.background.default,
                    },
                }}
            />
            <Box sx={{ py: 3 }}>
                {/* Header with Back Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton
                        onClick={handleBackClick}
                        sx={{
                            mr: 2,
                            color: 'primary.main',
                            '&:hover': {
                                bgcolor: 'action.hover',
                            }
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: 24 }} />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h4" component="h1" sx={{ color: 'text.primary' }}>
                                Data Principal Details
                            </Typography>
                            {dpData.isMinor && (
                                <Chip
                                    label="Minor"
                                    size="small"
                                    color="warning"
                                    sx={{ height: 24 }}
                                />
                            )}
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            DP ID: #{dpData.dpId}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Chip
                            label={formatIdentifier()}
                            variant="outlined"
                            icon={dpData.identifierType === 'EMAIL' ? <EmailIcon /> : <PhoneIcon />}
                            onClick={() => dpData.vaultId && handleViewIdentifier(dpData.vaultId)}
                            sx={{
                                cursor: dpData.vaultId ? 'pointer' : 'default',
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                '&:hover': dpData.vaultId ? {
                                    bgcolor: 'action.hover',
                                } : {}
                            }}
                        />
                        {dpData.isTest && (
                            <Tooltip
                                title={
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            Test Data
                                        </Typography>
                                        <Typography variant="caption">
                                            This data principal record contains test data. Identifier values are stored directly in the database (not in vault) and are visible for testing purposes.
                                        </Typography>
                                    </Box>
                                }
                                arrow
                                placement="top"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            maxWidth: 300,
                                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                            '& .MuiTooltip-arrow': {
                                                color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                            },
                                        },
                                    },
                                }}
                            >
                                <Chip label="Test Data" color="warning" size="small" sx={{ cursor: 'help' }} />
                            </Tooltip>
                        )}
                        <Tooltip
                            title={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {dpData.overallConsentStatus}
                                    </Typography>
                                    <Typography variant="caption">
                                        {getConsentStatusDescription(dpData.overallConsentStatus)}
                                    </Typography>
                                </Box>
                            }
                            arrow
                            placement="top"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: 300,
                                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                        '& .MuiTooltip-arrow': {
                                            color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                        },
                                    },
                                },
                            }}
                        >
                            <Chip
                                label={dpData.overallConsentStatus}
                                size="small"
                                sx={{
                                    bgcolor: statusColors.bg,
                                    color: statusColors.color,
                                    border: `1px solid ${statusColors.border}`,
                                    cursor: 'help',
                                }}
                            />
                        </Tooltip>
                    </Box>
                </Box>

                {/* Summary Information */}
                <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Identifier Type</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {dpData.identifierType === 'EMAIL' ? <EmailIcon fontSize="small" /> : <PhoneIcon fontSize="small" />}
                                {dpData.identifierType}
                            </Typography>
                        </Box>
                        {dpData.externalIdentifier && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">External ID</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {dpData.externalIdentifier}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography variant="caption" color="text.secondary">Consent Status</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {dpData.overallConsentStatus}
                            </Typography>
                        </Box>
                        {dpData.lastConsentDate && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Last Consent</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {new Date(dpData.lastConsentDate).toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                        {dpData.lastActivityDate && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Last Activity</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {new Date(dpData.lastActivityDate).toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography variant="caption" color="text.secondary">Created</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {new Date(dpData.createdDate).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* Tabs */}
                <Paper sx={{ bgcolor: 'background.paper' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                color: 'text.secondary',
                                '&.Mui-selected': {
                                    color: 'primary.main',
                                },
                            },
                        }}
                    >
                        {tabs.map((tab) => (
                            <Tab key={tab.value} label={tab.label} />
                        ))}
                    </Tabs>

                    {/* Tab Content */}
                    <Box sx={{ p: 3 }}>
                        <React.Suspense fallback={<CircularProgress />}>
                            {activeTab === 0 && dpData && <OverviewTab dpData={dpData} />}
                            {activeTab === 1 && dpData && <ConsentHistoryTab dpId={dpData.dpId} />}
                            {activeTab === 2 && dpData && <ConsentBreakdownTab dpId={dpData.dpId} />}
                            {activeTab === 3 && dpData && <RightsRequestsTab dpId={dpData.dpId} />}
                            {activeTab === 4 && dpData && <AssignedNoticesTab dpId={dpData.dpId} />}
                            {activeTab === 5 && dpData && <DataElementsTab dpId={dpData.dpId} />}
                            {activeTab === 6 && dpData && <ActivityLogTab dpId={dpData.dpId} />}
                        </React.Suspense>
                    </Box>
                </Paper>

                {/* Vault Value Dialog */}
                <Dialog
                    open={viewValueDialog}
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LockIcon />
                            <Typography variant="h6">View Identifier Value</Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {loadingValue ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ pt: 2 }}>
                                {valueError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {valueError}
                                    </Alert>
                                )}
                                {actualValue && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Actual Value
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                            <Typography variant="body1" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
                                                {actualValue}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleCopyToClipboard(actualValue)}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                )}
                                {maskedToken && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Masked Token
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                            <Typography variant="body1" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
                                                {maskedToken}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleCopyToClipboard(maskedToken)}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Copy Success Snackbar */}
                <Snackbar
                    open={copySuccess}
                    autoHideDuration={2000}
                    onClose={() => setCopySuccess(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={() => setCopySuccess(false)} severity="success" sx={{ width: '100%' }}>
                        Copied to clipboard!
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    );
};

export default DataPrincipalDetails;

